import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Brain, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { desfazerRegra, editarRegra, EVENTO_APRENDI } from '@/lib/cerebro';
import { mensagemDoErro } from '@/lib/mensagemDoErro';

export const TEMPO_DO_CHIP = 15000;

// Com um card aberto, o modal bloqueia clique e foco fora dele: o chip entra no próprio card, para Desfazer e Editar funcionarem.
const dialogoAberto = () => {
  const abertos = document.querySelectorAll('[role="dialog"][data-state="open"]');
  return abertos.length ? abertos[abertos.length - 1] : null;
};

/** O chip "Aprendi" (PROMPT 206, B3): cada regra nova numa frase, na hora, com Desfazer e Editar. Global, em qualquer tela. */
export function ChipAprendi() {
  const [regra, setRegra] = useState(null);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [dialogo, setDialogo] = useState(null);

  useEffect(() => {
    const chegou = (e) => { setRegra(e.detail); setEditando(false); setTexto(e.detail?.texto || ''); setDialogo(dialogoAberto()); };
    window.addEventListener(EVENTO_APRENDI, chegou);
    return () => window.removeEventListener(EVENTO_APRENDI, chegou);
  }, []);

  useEffect(() => {
    if (!regra || editando) return undefined;
    const t = setTimeout(() => setRegra(null), TEMPO_DO_CHIP);
    return () => clearTimeout(t);
  }, [regra, editando]);

  // O card fechou com o chip dentro: o chip volta para a tela.
  useEffect(() => {
    if (!regra || !dialogo) return undefined;
    const t = setInterval(() => { if (!document.contains(dialogo)) setDialogo(null); }, 400);
    return () => clearInterval(t);
  }, [regra, dialogo]);

  if (!regra) return null;

  const desfazer = async () => {
    try {
      await desfazerRegra(regra.id);
      toast.success('Desfeito. A regra saiu.');
      setRegra(null);
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não consegui desfazer. Tente de novo.'));
    }
  };
  const salvar = async () => {
    try {
      setRegra(await editarRegra(regra.id, texto));
      setEditando(false);
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não consegui salvar. Tente de novo.'));
    }
  };

  const chip = (
    <div role="status" aria-live="polite" data-testid="chip-aprendi" data-chip-aprendi="1"
      className={`z-[220] rounded-xl border border-border bg-card p-3 text-sm text-foreground shadow-lg ${dialogo ? 'sticky top-0 order-first mb-3' : 'fixed bottom-4 left-4 right-4 sm:left-auto sm:w-[380px]'}`}>
      <div className="flex items-start gap-2">
        <Brain className="mt-0.5 h-4 w-4 shrink-0 text-foreground" aria-hidden="true" />
        {editando ? (
          <Input value={texto} onChange={(e) => setTexto(e.target.value)} className="h-8" aria-label="Regra" data-testid="chip-aprendi-campo" />
        ) : (
          <p className="min-w-0 flex-1 break-words" data-testid="chip-aprendi-frase">{regra.frase}</p>
        )}
        <button type="button" onClick={() => setRegra(null)} aria-label="Fechar" className="shrink-0 text-muted-foreground"><X className="h-4 w-4" aria-hidden="true" /></button>
      </div>
      <div className="mt-2 flex justify-end gap-1">
        {editando ? (
          <Button type="button" size="sm" onClick={salvar} data-testid="chip-aprendi-salvar">Salvar</Button>
        ) : (
          <>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditando(true)} data-testid="chip-aprendi-editar">Editar</Button>
            <Button type="button" size="sm" variant="outline" onClick={desfazer} data-testid="chip-aprendi-desfazer">Desfazer</Button>
          </>
        )}
      </div>
    </div>
  );
  return dialogo ? createPortal(chip, dialogo) : chip;
}

export default ChipAprendi;
